import { Layers, TileLayer, VectorLayer } from "views/OLMap/Layers";
import { mapboxbase, xyz, googlebase } from "views/OLMap/Source";
import React, { useEffect } from "react";
import "views/OLMap/ol-map.css";
import MapContext from "views/OLMap/Map/MapContext";
import PropTypes from "prop-types";
import { fromLonLat } from "ol/proj";
import * as ol from "ol";
import { defaults as defaultControls } from "ol/control/defaults";
import { useSelector } from "react-redux";
import { geojsons } from "assets/geojson";
import { MousePosition } from "ol/control";
import { createStringXY } from "ol/coordinate";
import { getFeaturesSourceFromJson } from "utils/mapProcess";
import { OSM } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import Crop from "ol-ext/filter/Crop";
import { bingAerialLabels } from "../OLMap/Source";
import GridCard from "../Cards/GridCard";
import MKBox from "../../components/MKBox";

const GridCardMapAnalysis = ({ productionInfo, map, mapRef, setMap, className }) => {
  const dataSelection = useSelector((state) => state.dataSelection);
  const xyzurls = productionInfo.summaries.collection_tile_url;
  const viewOptions = {
    zoom: 4,
    center: fromLonLat([83, 28]),
    // extent: [5010000, 1300000, 13245143, 5560979],
  };
  const mousePositionControl = new MousePosition({
    coordinateFormat: createStringXY(4),
    projection: "EPSG:4326",
    className: "custom-mouse-position",
  });

  const vectorlayer =
    dataSelection.buildin !== "" && !dataSelection.draw ? (
      <VectorLayer
        key={dataSelection.buildin}
        visible
        className={dataSelection.buildin}
        source={getFeaturesSourceFromJson(dataSelection.buildin, geojsons)}
        zIndex={1}
      />
    ) : null;

  useEffect(() => {
    if (dataSelection.buildin !== "" && map !== null) {
      const FeatureJson = geojsons.filter((a) => a.name === dataSelection.buildin)[0].json;
      const geom = new GeoJSON().readFeatures(FeatureJson)[0].getGeometry();
      map.getView().fit(geom, {
        padding: [50, 50, 50, 50],
      });
    }
  }, [dataSelection.buildin]);

  const analysisLayers = [];
  if (
    productionInfo.id === "water_distribution_changes_analysis" ||
    productionInfo.id === "water_body_clarity_changes_analysis" ||
    productionInfo.id === "forest_grass_change" ||
    productionInfo.id === "fvc_change"
  ) {
    if (dataSelection.selectedData.length === 3) {
      xyzurls.map((item) =>
        analysisLayers.push({ analysisLayerUrl: item.url, analysisLayerName: item.year.join("-") })
      );
    } else if (dataSelection.selectedData.length !== 0) {
      const sortSelect = [...dataSelection.selectedData].sort();
      const analysisLayerUrl = xyzurls.filter(
        (item) =>
          item.year.length === dataSelection.selectedData.length &&
          item.year.every((value, index) => value === sortSelect[index])
      )[0].url;
      const analysisLayerName = [...dataSelection.selectedData].sort().join("-");
      analysisLayers.push({ analysisLayerName, analysisLayerUrl });
    }
  } else if (productionInfo.id === "water_good_quality_analysis") {
    if (dataSelection.selectedData.length !== 0) {
      dataSelection.selectedData.map((year) =>
        analysisLayers.push({
          analysisLayerName: year,
          analysisLayerUrl: xyzurls.filter((item) => item.year[0] === year)[0].url,
        })
      );
    }
  } else if (productionInfo.id === "water_distribution_frequency_analysis") {
    const sortSelect = [...dataSelection.selectedData].sort();
    const analysisLayerUrl =
      dataSelection.selectedData.length !== 0
        ? xyzurls.filter(
            (item) =>
              item.year.length === dataSelection.selectedData.length &&
              item.year.every((value, index) => value === sortSelect[index].toString())
          )[0].url
        : null;
    const analysisLayerName = dataSelection.selectedData.length !== 0 ? sortSelect.join("-") : null;
    analysisLayers.push({ analysisLayerName, analysisLayerUrl });
  }

  const analysisTilelayer =
    dataSelection.selectedData.length !== 0
      ? analysisLayers.map((item) => (
          <TileLayer
            key={item.analysisLayerUrl}
            className={item.analysisLayerName}
            source={xyz({
              url: `${item.analysisLayerUrl}`,
            })}
            visible
            zIndex={1}
          />
        ))
      : null;

  const basemapStatus = useSelector((state) => state.dataLayers);
  const baseLayers = (
    <>
      {basemapStatus.filter((basemap) => basemap.name === "mapbox")[0].selected ? (
        <TileLayer source={mapboxbase()} className="mapbox" baseLayer zIndex={0} visible />
      ) : null}
      {basemapStatus.filter((basemap) => basemap.name === "google")[0].selected ? (
        <TileLayer source={googlebase()} className="google" baseLayer zIndex={0} visible />
      ) : null}
      {/* {basemapStatus.filter((basemap) => basemap.name === "bing-Aerial")[0].selected ? ( */}
      {/*   <TileLayer source={bingAerial()} className="bingAerial" baseLayer zIndex={0} visible /> */}
      {/* ) : null} */}
      {basemapStatus.filter((basemap) => basemap.name === "bing-Aerial-labels")[0].selected ? (
        <TileLayer
          source={bingAerialLabels()}
          className="bingAerialLables"
          baseLayer
          zIndex={0}
          visible
        />
      ) : null}{" "}
      {basemapStatus.filter((basemap) => basemap.name === "openStreetMap")[0].selected ? (
        <TileLayer source={new OSM()} className="osm" baseLayer zIndex={0} visible />
      ) : null}
    </>
  );

  // on component mount
  useEffect(() => {
    const options = {
      view: new ol.View(viewOptions),
      layers: [],
      controls: defaultControls().extend([mousePositionControl]),
      overlays: [],
    };
    const mapObject = new ol.Map(options);
    mapObject.setTarget(mapRef.current);
    setMap(mapObject);
    return () => mapObject.setTarget(undefined);
  }, []);

  const viewInROI = (name, clipLayer) => {
    const tileLayer = map
      .getLayers()
      .getArray()
      .filter((t) => t.getClassName() === name.toString())[0];
    if (tileLayer.getFilters() !== []) {
      tileLayer.getFilters().map((f) => tileLayer.removeFilter(f));
    }
    clipLayer.getSource().forEachFeature((f) => {
      const crop = new Crop({ feature: f, wrapX: true, inner: false });
      crop.setActive(true);
      tileLayer.addFilter(crop);
    });
  };

  useEffect(() => {
    if (!map) return;
    const clipLayerName = dataSelection.draw ? "draw" : dataSelection.buildin;
    const clipLayer = map
      .getLayers()
      .getArray()
      .find((t) => t.getClassName() === clipLayerName);
    if (
      dataSelection.selectedData.length !== 0 &&
      clipLayer !== undefined &&
      (dataSelection.draw || dataSelection.buildin !== null)
    ) {
      analysisLayers.forEach((layer) => viewInROI(layer.analysisLayerName, clipLayer));
    }
  }, [dataSelection]);

  return (
    <GridCard title={productionInfo.summaries.map_title}>
      <MKBox sx={{ px: 1, pb: 1 }}>
        <MapContext.Provider value={{ map }}>
          <div ref={mapRef} className={className}>
            <Layers>
              {baseLayers}
              {analysisTilelayer}
              {vectorlayer}
            </Layers>
          </div>
        </MapContext.Provider>
      </MKBox>
    </GridCard>
  );
};

GridCardMapAnalysis.defaultProps = {
  map: null,
  setMap: null,
  mapRef: null,
  productionInfo: {},
  className: null,
};

GridCardMapAnalysis.propTypes = {
  productionInfo: PropTypes.oneOfType([PropTypes.object]),
  map: PropTypes.oneOfType([PropTypes.any]),
  setMap: PropTypes.oneOfType([PropTypes.any]),
  mapRef: PropTypes.oneOfType([PropTypes.any]),
  className: PropTypes.string,
};

export default GridCardMapAnalysis;
